import React, { createContext, useContext, useRef } from 'react';
import { YouTubeMedia } from '../../babylon/World/Types';

export interface MediaPlayerControlsContextValue {
  updateYouTubeVideoId: (videoId: string) => void;
  onUpdateYouTubeVideoId: (callback: (videoId: string) => void) => () => void;
  updatedYouTubeVideoId: (youTubeMedia: YouTubeMedia) => void;
  onUpdatedYouTubeVideoId: (callback: (youTubeMedia: YouTubeMedia) => void) => () => void;
  setPlaybackRate: (rate: number) => void;
  onSetPlaybackRate: (callback: (rate: number) => void) => () => void;
  setVolume: (rate: number) => void;
  onSetVolume: (callback: (volume: number) => void) => () => void;
  setSeek: (seek: number) => void;
  onSetSeek: (callback: (seek: number) => void) => () => void;
  setMute: (mute: boolean) => void;
  onSetMute: (callback: (mute: boolean) => void) => () => void;
  setPause: (pause: boolean) => void;
  onSetPause: (callback: (pause: boolean) => void) => () => void;
}

const MediaPlayerControlsContext = createContext<MediaPlayerControlsContextValue | undefined>(undefined);

export const MediaPlayerControlsProvider = ({ children }: any) => {
  const updateYouTubeVideoIdCallbacks = useRef<((videoId: string) => void)[]>([]);
  const updatedYouTubeVideoIdCallbacks = useRef<((youTubeMedia: YouTubeMedia) => void)[]>([]);
  const setPlaybackRateCallbacks = useRef<((rate: number) => void)[]>([]);
  const setVolumeCallbacks = useRef<((volume: number) => void)[]>([]);
  const setSeekCallbacks = useRef<((seek: number) => void)[]>([]);
  const setMuteCallbacks = useRef<((mute: boolean) => void)[]>([]);
  const setPauseCallbacks = useRef<((pause: boolean) => void)[]>([]);

  const triggerCallbacks = (callbacks: React.MutableRefObject<((arg: any) => void)[]>, arg: any) => {
    callbacks.current.forEach(callback => callback(arg));
  };

  const subscribe = (callbacks: React.MutableRefObject<((arg: any) => void)[]>, callback: (arg: any) => void) => {
    callbacks.current.push(callback);
    return () => {
      const index = callbacks.current.indexOf(callback);
      if (index > -1) {
        callbacks.current.splice(index, 1);
      }
    };
  };

  const value: MediaPlayerControlsContextValue = {
    updateYouTubeVideoId: (videoId: string) => triggerCallbacks(updateYouTubeVideoIdCallbacks, videoId),
    onUpdateYouTubeVideoId: (callback: (videoId: string) => void) => subscribe(updateYouTubeVideoIdCallbacks, callback),
    updatedYouTubeVideoId: (youTubeMedia: YouTubeMedia) => triggerCallbacks(updatedYouTubeVideoIdCallbacks, youTubeMedia),
    onUpdatedYouTubeVideoId: (callback: (youTubeMedia: YouTubeMedia) => void) => subscribe(updatedYouTubeVideoIdCallbacks, callback),
    setPlaybackRate: (rate: number) => triggerCallbacks(setPlaybackRateCallbacks, rate),
    onSetPlaybackRate: (callback: (rate: number) => void) => subscribe(setPlaybackRateCallbacks, callback),
    setVolume: (rate: number) => triggerCallbacks(setVolumeCallbacks, rate),
    onSetVolume: (callback: (rate: number) => void) => subscribe(setVolumeCallbacks, callback),
    setSeek: (seek: number) => triggerCallbacks(setSeekCallbacks, seek),
    onSetSeek: (callback: (seek: number) => void) => subscribe(setSeekCallbacks, callback),
    setMute: (mute: boolean) => triggerCallbacks(setMuteCallbacks, mute),
    onSetMute: (callback: (mute: boolean) => void) => subscribe(setMuteCallbacks, callback),
    setPause: (pause: boolean) => triggerCallbacks(setPauseCallbacks, pause),
    onSetPause: (callback: (pause: boolean) => void) => subscribe(setPauseCallbacks, callback),
  };

  return (
    <MediaPlayerControlsContext.Provider value={value}>
      {children}
    </MediaPlayerControlsContext.Provider>
  );
};

export const useMediaPlayerControls = () => {
  const context = useContext(MediaPlayerControlsContext);
  if (context === undefined) {
    throw new Error('useMediaPlayerControls must be used within a MediaPlayerControlsProvider');
  }
  return context;
};
